import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useSupabaseAuth } from '../../providers/SupabaseAuthProvider';
import servicesService from '../../services/servicesService';
import OTPVerificationModal from './OTPVerificationModal';
import './ServiceAuthModal.css';

/**
 * ServiceAuthModal - Email authentication modal for community services
 * Uses Supabase Auth with password-based authentication
 */
function ServiceAuthModal({ config, onClose, onSuccess, theme }) {
  // Use auth context from provider
  const { session, user } = useSupabaseAuth();

  const [step, setStep] = useState('email'); // 'email', 'signin', 'signup', 'profile', 'forgot-password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [error, setError] = useState('');
  const [userExists, setUserExists] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Profile completion fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [recognitionDescription, setRecognitionDescription] = useState('');
  const [isNewUser, setIsNewUser] = useState(false); // Track if user is new or existing

  // WhatsApp and signup fields
  const [confirmPassword, setConfirmPassword] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = useState(true);

  // Email confirmation modal state
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Account created success modal state
  const [showAccountCreated, setShowAccountCreated] = useState(false);
  const [createdMemberData, setCreatedMemberData] = useState(null);

  // OTP verification modal state
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [tempPassword, setTempPassword] = useState(''); // Store password temporarily for auto-signin after OTP

  // Check if user is already authenticated (using context)
  useEffect(() => {
    if (session && user) {
      handleAuthenticatedUser(user);
    }
  }, [session, user]);

  const handleAuthenticatedUser = async (user) => {
    // Check if user just confirmed email via OTP (set by SupabaseAuthProvider)
    const justConfirmed = sessionStorage.getItem('auth:justConfirmed') === 'true';
    if (justConfirmed) {
      console.log('[handleAuthenticatedUser] Just confirmed email via OTP - clearing flag and continuing');
      // Clear flag immediately so it doesn't interfere with subsequent operations
      sessionStorage.removeItem('auth:justConfirmed');
    }

    console.log('[handleAuthenticatedUser] Fetching member data for user:', user.id);

    try {
      // Check if profile exists and is completed
      const { data: userData, error: userError } = await supabase
        .from('verified_users')
        .select('*, community_members!user_id(id, center_id, membership_status, city, state)')
        .eq('id', user.id)
        .maybeSingle(); // Use maybeSingle() to handle case where user doesn't exist

      if (userError) {
        console.error('Error fetching user data:', userError);
        setError('Database error. Please try again.');
        return;
      }

      // Handle case where user doesn't have a profile yet (new user)
      if (!userData) {
        console.log('[ServiceAuthModal] User authenticated but no profile exists. Showing profile form.');
        setStep('profile');
        return;
      }

      if (!userData.profile_completed) {
        // Show profile completion form
        setStep('profile');
      } else {
        // Check if community member exists for this center
        const existingMember = userData.community_members?.find(
          m => m.center_id === config.center_id
        );

        if (existingMember) {
          // Member exists, return their info
          const memberData = {
            id: existingMember.id,
            user_id: user.id,
            name: userData.first_name,
            last_initial: userData.last_name?.charAt(0) || '',
            email: user.email,
            status: existingMember.membership_status,
            city: existingMember.city,
            state: existingMember.state
          };
          console.log('[handleAuthenticatedUser] Calling onSuccess with existing member data:', memberData);
          onSuccess(memberData);
        } else {
          // Create new community member for this center
          const { data: createResult, error: createError } = await supabase
            .rpc('create_community_member', {
              p_user_id: user.id,
              p_center_id: config.center_id
            });

          if (createError) {
            console.error('Error creating community member:', createError);
            setError('Failed to join community');
            return;
          }

          const memberData = {
            id: createResult.member_id,
            user_id: user.id,
            name: userData.first_name,
            last_initial: userData.last_name?.charAt(0) || '',
            email: user.email,
            status: createResult.status,
            city: userData.city,
            state: userData.state
          };
          console.log('[handleAuthenticatedUser] Calling onSuccess with new member data:', memberData);
          onSuccess(memberData);
        }
      }
    } catch (err) {
      console.error('Error handling authenticated user:', err);
      setError('An error occurred. Please try again.');
    }
  };

  // Check if email exists when user leaves the email field
  const checkEmailExists = async (emailToCheck) => {
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setUserExists(null);
      return;
    }

    setCheckingEmail(true);
    setError('');

    try {
      const { data, error: checkError } = await supabase
        .rpc('check_user_exists', {
          p_identifier: emailToCheck.trim().toLowerCase()
        });

      if (checkError) {
        console.error('Error checking user:', checkError);
        setUserExists(null);
      } else {
        const exists = data?.exists || false;
        const isLocked = data?.locked || false;

        setUserExists(exists);
        setIsNewUser(!exists);

        // Handle locked accounts
        if (isLocked) {
          setError('Account is locked. Please contact support or try password reset.');
          setUserExists(true); // Still show password field so they can try forgot password
        }
      }
    } catch (err) {
      console.error('Error checking email:', err);
      setUserExists(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  // Handle sign in with password
  const handleSignIn = async (e) => {
    e.preventDefault();

    // Validation: Don't submit if email doesn't exist
    if (userExists !== true) {
      setError('Please enter a valid registered email address');
      return;
    }

    // Validation: Don't submit if password is empty
    if (!password || password.trim() === '') {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    setError('');
    setResetEmailSent(false);

    try {
      const loginEmail = email.trim().toLowerCase();

      // Sign in with password
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password
      });

      if (error) {
        // Track failed login attempt
        await supabase.rpc('track_failed_login', {
          p_email: loginEmail
        });
        throw error;
      }

      // Update last login timestamp
      await supabase.rpc('update_last_login', {
        p_user_id: data.user.id
      });

      // Check if authenticated user has profile completed
      // This will fetch member data and call onSuccess() with correct structure
      // MUST AWAIT to ensure completion before closing modal
      await handleAuthenticatedUser(data.user);
      console.log('[handleSignIn] Member data fetched and onSuccess called, now closing modal');

      // Note: onSuccess() is called by handleAuthenticatedUser() with proper member data
      // Don't call it again here to avoid overwriting with wrong data structure

      // Close modal after successful sign-in
      // This now happens AFTER handleAuthenticatedUser completes
      onClose();
    } catch (err) {
      console.error('Sign in error:', err);
      if (err.message.includes('Invalid login credentials')) {
        setError('Incorrect password. Try "Forgot Password?" to reset it.');
      } else if (err.message.includes('Email not confirmed')) {
        setError('Please verify your email first. Check your inbox for the verification link.');
      } else if (err.message.includes('locked')) {
        setError('Account is temporarily locked. Please try password reset or contact support.');
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter your email address first');
      return;
    }

    setLoading(true);
    setError('');
    setResetEmailSent(false);

    try {
      const resetEmail = email.trim().toLowerCase();

      // Send password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setResetEmailSent(true);
      setError('');
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle WhatsApp toggle
  const handleWhatsappCheckboxChange = (checked) => {
    setWhatsappSameAsPhone(checked);
    if (checked) {
      setWhatsappNumber(phoneNumber);
    } else {
      setWhatsappNumber('');
    }
  };

  // Handle combined sign up (password + profile in one submission)
  const handleCombinedSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('[handleCombinedSignUp] Starting combined registration...');

    // STEP 1: Validate password fields
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // STEP 2: Validate required profile fields
    if (!firstName.trim() || !lastName.trim() || !phoneNumber.trim()) {
      setError('Please fill in all required fields (name and phone number)');
      setLoading(false);
      return;
    }

    try {
      const signupEmail = email.trim().toLowerCase();

      console.log('[handleCombinedSignUp] Creating Supabase auth user...');

      // STEP 3: Create auth user with Supabase (NO emailRedirectTo = sends OTP code)
      const { data, error } = await supabase.auth.signUp({
        email: signupEmail,
        password: password,
        options: {
          // NO emailRedirectTo - this triggers OTP code instead of link
          data: {
            center_id: config.center_id,
            center_name: config.name,
            center_domain: config.domain || window.location.hostname
          }
        }
      });

      if (error) throw error;

      if (!data.user) {
        throw new Error('User creation failed - no user returned');
      }

      console.log('[handleCombinedSignUp] User created successfully:', data.user.id);

      // Check if email confirmation is required (Expert-recommended pattern)
      const requiresConfirmation = !data.session && !data.user.email_confirmed_at;

      if (requiresConfirmation) {
        console.log('[handleCombinedSignUp] OTP verification required - creating profile immediately');
        console.log('[handleCombinedSignUp] User ID:', data.user.id);
        console.log('[handleCombinedSignUp] User email:', data.user.email);

        // Create profile immediately (RPC functions have anon permissions)
        console.log('[handleCombinedSignUp] Creating profile immediately...');

        const rpcParams = {
          p_user_id: data.user.id,
          p_email: signupEmail,
          p_first_name: firstName.trim(),
          p_last_name: lastName.trim(),
          p_phone_number: phoneNumber.trim(),
          p_whatsapp_number: whatsappSameAsPhone ? phoneNumber.trim() : (whatsappNumber.trim() || phoneNumber.trim()),
          p_city: city.trim() || null,
          p_state: state.trim() || null,
          p_zip_code: null,
          p_recognition_description: recognitionDescription.trim() || null
        };

        console.log('[handleCombinedSignUp] RPC params:', JSON.stringify(rpcParams, null, 2));

        try {
          const { data: profileResult, error: profileError } = await supabase
            .rpc('complete_user_profile', rpcParams);

          console.log('[handleCombinedSignUp] Profile RPC response:', {
            data: profileResult,
            error: profileError
          });

          if (profileError) {
            console.error('[handleCombinedSignUp] CRITICAL: Profile creation failed:', {
              message: profileError.message,
              details: profileError.details,
              hint: profileError.hint,
              code: profileError.code,
              full_error: profileError
            });
            setError(`Failed to create profile: ${profileError.message || 'Unknown error'}`);
            setLoading(false);
            return;
          }

          // Check if the function returned an error in the data
          if (profileResult && typeof profileResult === 'object') {
            if (profileResult.success === false) {
              console.error('[handleCombinedSignUp] Profile creation returned error:', profileResult);
              setError(profileResult.message || 'Failed to create profile');
              setLoading(false);
              return;
            }
            console.log('[handleCombinedSignUp] Profile created successfully!', profileResult);
          } else {
            console.warn('[handleCombinedSignUp] Unexpected profile result format:', profileResult);
          }

          // Create community member
          console.log('[handleCombinedSignUp] Creating community member...');
          const { data: memberResult, error: memberError } = await supabase
            .rpc('create_community_member', {
              p_user_id: data.user.id,
              p_center_id: config.center_id
            });

          console.log('[handleCombinedSignUp] Member RPC response:', {
            data: memberResult,
            error: memberError
          });

          if (memberError) {
            console.error('[handleCombinedSignUp] CRITICAL: Member creation failed:', {
              message: memberError.message,
              details: memberError.details,
              hint: memberError.hint,
              code: memberError.code,
              full_error: memberError
            });
            setError(`Failed to join community: ${memberError.message || 'Unknown error'}`);
            setLoading(false);
            return;
          }

          console.log('[handleCombinedSignUp] Both profile and member created successfully!');

          // Set member info and show success
          const memberData = {
            id: memberResult?.member_id || data.user.id,
            user_id: data.user.id,
            name: firstName.trim(),
            last_initial: lastName?.charAt(0) || '',
            email: signupEmail,
            status: 'provisional', // Email not verified yet
            auto_approved: profileResult?.auto_approved || false,
            message: 'Account created! You can now post services. Please verify your email within 7 days to maintain access.'
          };

          // Store for session persistence
          servicesService.saveMemberInfo(memberData);

          // Store credentials for auto-signin after OTP
          setOtpEmail(signupEmail);
          setTempPassword(password);

          // Send OTP code email (not a link)
          console.log('[handleCombinedSignUp] Sending OTP code email...');
          const { error: otpError } = await supabase.auth.signInWithOtp({
            email: signupEmail,
            options: {
              shouldCreateUser: false, // User already created
              // NO emailRedirectTo = sends code instead of link
              data: {
                service: 'community_services',
                service_label: 'Community Services',
                center_name: config.name,
                center_logo: config.logo_url || '',
                center_color: config.colors?.primary?.replace('#', '') || '',
                center_id: config.center_id,
              }
            }
          });

          if (otpError) {
            console.error('[handleCombinedSignUp] Failed to send OTP:', otpError);
            // Continue anyway - user can request new code
          }

          // Show OTP modal (REQUIRED - user must verify to proceed)
          setShowOTPModal(true);
          setShowAccountCreated(false);
          setShowEmailConfirmation(false);

          console.log('[handleCombinedSignUp] Registration complete - showing OTP modal (verification required)');
          setLoading(false);
          return;

        } catch (rpcError) {
          console.error('[handleCombinedSignUp] Unexpected RPC error:', rpcError);
          setError('An unexpected error occurred. Please try again.');
          setLoading(false);
          return;
        }
      }

      console.log('[handleCombinedSignUp] Auto-confirmed - proceeding with profile creation');

      // Only execute this block if user is auto-confirmed (no email confirmation required)
      // STEP 4: Create complete user profile immediately
      const rpcParams = {
        p_user_id: data.user.id,
        p_email: signupEmail,
        p_first_name: firstName.trim(),
        p_last_name: lastName.trim(),
        p_phone_number: phoneNumber.trim(),
        p_whatsapp_number: whatsappSameAsPhone ? phoneNumber.trim() : (whatsappNumber.trim() || phoneNumber.trim()),
        p_city: city.trim() || null,
        p_state: state.trim() || null,
        p_zip_code: null,
        p_recognition_description: recognitionDescription.trim() || null
      };

      console.log('[handleCombinedSignUp] RPC params:', JSON.stringify(rpcParams, null, 2));

      const { data: profileResult, error: profileError } = await supabase
        .rpc('complete_user_profile', rpcParams);

      if (profileError) {
        console.error('[handleCombinedSignUp] Profile creation error:', profileError);
        throw new Error(`Failed to create profile: ${profileError.message}`);
      }

      console.log('[handleCombinedSignUp] Profile created successfully!');
      console.log('[handleCombinedSignUp] Creating community member...');

      // STEP 5: Create community member
      const { data: memberResult, error: memberError } = await supabase
        .rpc('create_community_member', {
          p_user_id: data.user.id,
          p_center_id: config.center_id
        });

      if (memberError) {
        console.error('[handleCombinedSignUp] Community member creation error:', memberError);
        throw new Error(`Failed to join community: ${memberError.message}`);
      }

      console.log('[handleCombinedSignUp] Community member created successfully!');
      console.log('[handleCombinedSignUp] Registration complete! Calling onSuccess...');

      // STEP 6: Success! Return member info
      onSuccess({
        id: memberResult.member_id,
        user_id: data.user.id,
        name: firstName,
        last_initial: lastName?.charAt(0) || '',
        email: signupEmail,
        status: memberResult.status,
        auto_approved: profileResult.auto_approved,
        message: profileResult.message
      });

      setLoading(false);
    } catch (err) {
      console.error('[handleCombinedSignUp] Error:', err);
      if (err.message.includes('already registered')) {
        setError('This email is already registered. Please sign in instead.');
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    console.log('[handleCompleteProfile] Function called');
    console.log('[handleCompleteProfile] Form data:', { firstName, lastName, phoneNumber, city, state, recognitionDescription });

    setError('');
    setLoading(true);

    // Validate required fields
    if (!firstName.trim() || !lastName.trim() || !phoneNumber.trim()) {
      console.log('[handleCompleteProfile] Validation failed - missing required fields');
      setError('Please fill in all required fields (name and phone number)');
      setLoading(false);
      return;
    }

    console.log('[handleCompleteProfile] Validation passed, proceeding...');

    try {
      console.log('[handleCompleteProfile] Getting current user...');
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.log('[handleCompleteProfile] ERROR: User not authenticated');
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      console.log('[handleCompleteProfile] User authenticated:', user.id);
      console.log('[handleCompleteProfile] Calling complete_user_profile RPC...');

      // Call complete_user_profile function
      const rpcParams = {
        p_user_id: user.id,
        p_email: user.email,  // Pass the email from authenticated user
        p_first_name: firstName.trim(),
        p_last_name: lastName.trim(),
        p_phone_number: phoneNumber.trim(),
        p_whatsapp_number: whatsappSameAsPhone ? phoneNumber.trim() : (whatsappNumber.trim() || phoneNumber.trim()),
        p_city: city.trim() || null,
        p_state: state.trim() || null,
        p_zip_code: null, // Zip code field removed from form
        p_recognition_description: recognitionDescription.trim() || null
      };

      console.log('[handleCompleteProfile] RPC params being sent:', JSON.stringify(rpcParams, null, 2));

      const { data: result, error: profileError} = await supabase
        .rpc('complete_user_profile', rpcParams);

      console.log('[handleCompleteProfile] RPC complete_user_profile response:', { result, profileError });

      if (profileError) {
        console.error('[handleCompleteProfile] ERROR completing profile:');
        console.error('  Error code:', profileError.code);
        console.error('  Error message:', profileError.message);
        console.error('  Error details:', profileError.details);
        console.error('  Error hint:', profileError.hint);
        console.error('  Full error object:', JSON.stringify(profileError, null, 2));

        // Provide more specific error messages based on error code
        if (profileError.code === '23502') {
          setError(`Database constraint error: ${profileError.message}. Please contact support.`);
        } else if (profileError.code === '42883') {
          setError('Function error. Please contact support.');
        } else {
          setError(`Failed to save profile: ${profileError.message || 'Unknown error'}`);
        }

        setLoading(false);
        return;
      }

      console.log('[handleCompleteProfile] Profile saved successfully!');
      console.log('[handleCompleteProfile] Creating community member...');

      // Create community member
      const { data: memberResult, error: memberError } = await supabase
        .rpc('create_community_member', {
          p_user_id: user.id,
          p_center_id: config.center_id
        });

      console.log('[handleCompleteProfile] RPC create_community_member response:', { memberResult, memberError });

      if (memberError) {
        console.error('[handleCompleteProfile] ERROR creating community member:', memberError);
        setError('Failed to join community. Please try again.');
        setLoading(false);
        return;
      }

      // Success!
      console.log('[handleCompleteProfile] SUCCESS! Calling onSuccess callback...');
      onSuccess({
        id: memberResult.member_id,
        user_id: user.id,
        name: firstName,
        last_initial: lastName?.charAt(0) || '',
        email: user.email,
        status: memberResult.status,
        auto_approved: result.auto_approved,
        message: result.message,
        city: city.trim() || null,
        state: state.trim() || null
      });

      console.log('[handleCompleteProfile] Profile completion flow finished successfully!');
      setLoading(false);
    } catch (err) {
      console.error('[handleCompleteProfile] EXCEPTION caught:', err);
      console.error('[handleCompleteProfile] Exception stack:', err.stack);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const renderEmailStep = () => (
    <form onSubmit={handleSignIn}>
      {/* Email Field - Always Visible */}
      <div className="form-group">
        <label>Email Address</label>
        <input
          type="email"
          placeholder="your.email@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // Reset user exists state when email changes
            if (userExists !== null) {
              setUserExists(null);
              setError('');
            }
          }}
          onBlur={(e) => checkEmailExists(e.target.value)}
          required
          className="form-input"
          autoFocus
          disabled={checkingEmail}
        />
        {checkingEmail && (
          <p className="form-hint" style={{ fontStyle: 'italic', color: '#666', marginTop: '5px' }}>
            Checking email...
          </p>
        )}
        {userExists === false && !checkingEmail && (
          <p className="form-hint" style={{ color: '#f44336', marginTop: '5px' }}>
            ⚠️ Email not found in our system
          </p>
        )}
        {userExists === true && !checkingEmail && (
          <p className="form-hint" style={{ color: '#4CAF50', marginTop: '5px' }}>
            ✓ Email found
          </p>
        )}
      </div>

      {/* Password Field - Always Visible */}
      <div className="form-group">
        <label>Password</label>
        <div style={{ position: 'relative' }}>
          <input
            type={showPassword ? "text" : "password"}
            placeholder={userExists === false ? "Please register first" : "Enter your password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required={userExists === true}
            disabled={userExists === false}
            className="form-input"
            style={{
              background: userExists === false ? '#f5f5f5' : 'white',
              cursor: userExists === false ? 'not-allowed' : 'text'
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            disabled={userExists === false}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: userExists === false ? 'not-allowed' : 'pointer',
              fontSize: '18px',
              opacity: userExists === false ? 0.3 : 0.6
            }}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
        {/* Forgot Password - Always show */}
        <button
          type="button"
          onClick={handleForgotPassword}
          disabled={userExists !== true || loading}
          style={{
            background: 'none',
            border: 'none',
            color: userExists === true && !loading ? '#4CAF50' : '#ccc',
            textDecoration: 'underline',
            cursor: userExists === true && !loading ? 'pointer' : 'not-allowed',
            fontSize: '0.9rem',
            marginTop: '5px',
            padding: 0
          }}
        >
          {loading ? 'Sending...' : 'Forgot Password?'}
        </button>

        {/* Password Reset Success Message */}
        {resetEmailSent && (
          <div style={{
            marginTop: '10px',
            padding: '10px',
            background: '#e8f5e9',
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#2e7d32'
          }}>
            ✅ Password reset email sent! Check your inbox (and spam folder).
          </div>
        )}
      </div>

      {/* Register Button - Only show if user doesn't exist */}
      {userExists === false && !checkingEmail && (
        <div style={{ textAlign: 'center', padding: '20px', background: '#f9f9f9', borderRadius: '8px', marginTop: '15px' }}>
          <p style={{ marginBottom: '15px', color: '#666' }}>
            Email doesn't exist. Please register.
          </p>
          <button
            type="button"
            onClick={() => setStep('signup')}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 30px' }}
          >
            Register
          </button>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="modal-footer">
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        {/* Sign In Button - Only show if user exists */}
        {userExists === true && (
          <button type="submit" className="btn-primary" disabled={loading || !password}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        )}
      </div>

      <div className="privacy-note">
        <p>
          🔒 Your email is private and never shared publicly.
          Community members can only contact you through WhatsApp when you post a service.
        </p>
      </div>
    </form>
  );

  const renderSignInStep = () => (
    <form onSubmit={handleSignIn}>
      <div className="form-group">
        <label>Email Address</label>
        <input
          type="email"
          value={email}
          disabled
          className="form-input"
          style={{ background: '#f5f5f5' }}
        />
        <button
          type="button"
          className="link-button"
          onClick={() => {
            setStep('email');
            setPassword('');
            setUserExists(null);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#4CAF50',
            cursor: 'pointer',
            fontSize: '0.9rem',
            marginTop: '5px',
            textDecoration: 'underline'
          }}
        >
          Use different email
        </button>
      </div>

      <div className="form-group">
        <label>Password</label>
        <div style={{ position: 'relative' }}>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="form-input"
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
        <button
          type="button"
          onClick={() => setStep('forgot-password')}
          style={{
            background: 'none',
            border: 'none',
            color: '#4CAF50',
            cursor: 'pointer',
            fontSize: '0.9rem',
            marginTop: '5px',
            textDecoration: 'underline'
          }}
        >
          Forgot Password?
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="modal-footer">
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={loading || !password}>
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </div>
    </form>
  );

  const renderSignUpPrompt = () => (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <h3>New to Our Community?</h3>
      <p style={{ margin: '15px 0', fontSize: '1rem' }}>
        Account for <strong>{email}</strong> not found.
      </p>
      <p style={{ margin: '15px 0', color: '#666' }}>
        Registration will be available in the next update. For now, please contact an administrator to create your account.
      </p>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => {
          setStep('email');
          setEmail('');
          setUserExists(null);
        }}
        style={{ marginTop: '20px' }}
      >
        Try Different Email
      </button>
    </div>
  );

  const renderForgotPasswordStep = () => (
    <form onSubmit={handleForgotPassword}>
      <div className="form-group">
        <label>Email Address</label>
        <input
          type="email"
          value={email}
          disabled
          className="form-input"
          style={{ background: '#f5f5f5' }}
        />
        <p className="form-hint" style={{ marginTop: '10px' }}>
          We'll send you a link to reset your password.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="modal-footer">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setStep('signin');
            setError('');
          }}
        >
          Back to Sign In
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Sending...' : 'Send Reset Link'}
        </button>
      </div>
    </form>
  );

  const renderCombinedSignUpForm = () => (
    <form onSubmit={handleCombinedSignUp}>
      <div className="profile-form-intro">
        <p><strong>🎉 Join Our Community!</strong></p>
        <p>Complete all fields below to create your account</p>
      </div>

      {/* Account Information Section */}
      <div className="form-section" style={{ marginBottom: '25px' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', color: '#333', borderBottom: '2px solid #e0e0e0', paddingBottom: '8px' }}>
          📧 Account Information
        </h3>

        <div className="form-group">
          <label>Email Address</label>
          <input
            type="email"
            value={email}
            disabled
            className="form-input"
            style={{ background: '#f5f5f5' }}
          />
        </div>

        <div className="form-group">
          <label>Create Password *</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="At least 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="form-input"
              minLength="6"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          <p className="form-hint">Minimum 6 characters</p>
        </div>

        <div className="form-group">
          <label>Confirm Password *</label>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="form-input"
            minLength="6"
          />
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="form-section" style={{ marginBottom: '25px' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', color: '#333', borderBottom: '2px solid #e0e0e0', paddingBottom: '8px' }}>
          👤 Personal Information
        </h3>

        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              className="form-input"
            />
          </div>
        </div>
        <p className="form-hint" style={{ marginTop: '-15px', marginBottom: '15px' }}>
          Only your last initial will be shown publicly (e.g., "John D.")
        </p>
      </div>

      {/* Contact Information Section */}
      <div className="form-section" style={{ marginBottom: '25px' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', color: '#333', borderBottom: '2px solid #e0e0e0', paddingBottom: '8px' }}>
          📱 Contact Information
        </h3>

        <div className="form-group">
          <label>Phone Number *</label>
          <input
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              if (whatsappSameAsPhone) {
                setWhatsappNumber(e.target.value);
              }
            }}
            required
            className="form-input"
          />
          <p className="form-hint" style={{ marginTop: '5px', marginBottom: '0' }}>
            We may call you to verify your profile for community safety
          </p>
        </div>

        <div className="form-group">
          <label>WhatsApp Number (Optional)</label>
          <input
            type="tel"
            placeholder="+1 (555) 987-6543"
            value={whatsappNumber}
            onChange={(e) => setWhatsappNumber(e.target.value)}
            disabled={whatsappSameAsPhone}
            className="form-input"
            style={{
              background: whatsappSameAsPhone ? '#f5f5f5' : 'white',
              cursor: whatsappSameAsPhone ? 'not-allowed' : 'text'
            }}
          />

          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              id="whatsapp-same-signup"
              checked={whatsappSameAsPhone}
              onChange={(e) => handleWhatsappCheckboxChange(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <label
              htmlFor="whatsapp-same-signup"
              style={{ cursor: 'pointer', fontSize: '0.95rem', margin: 0 }}
            >
              Same as phone number
            </label>
          </div>

          <p className="form-hint" style={{ marginTop: '5px' }}>
            Community members will contact you via WhatsApp when responding to your services
          </p>
        </div>
      </div>

      {/* Location Section */}
      <div className="form-section" style={{ marginBottom: '25px' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', color: '#333', borderBottom: '2px solid #e0e0e0', paddingBottom: '8px' }}>
          📍 Location (Optional)
        </h3>

        <div className="form-row">
          <div className="form-group">
            <label>City</label>
            <input
              type="text"
              placeholder="San Francisco"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>State</label>
            <input
              type="text"
              placeholder="CA"
              value={state}
              onChange={(e) => setState(e.target.value.slice(0, 2).toUpperCase())}
              className="form-input"
              maxLength="2"
              style={{ textTransform: 'uppercase' }}
            />
          </div>
        </div>
      </div>

      {/* Recognition Section */}
      <div className="form-section" style={{ marginBottom: '25px' }}>
        <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', color: '#333', borderBottom: '2px solid #e0e0e0', paddingBottom: '8px' }}>
          👋 Help Us Recognize You
        </h3>

        <div className="form-group">
          <label>How would you describe yourself?</label>
          <textarea
            placeholder="Describe yourself in 1-2 lines so community members recognize you..."
            value={recognitionDescription}
            onChange={(e) => setRecognitionDescription(e.target.value)}
            className="form-input form-textarea"
            rows="3"
            maxLength="200"
          />
          <div className="recognition-examples">
            <p><strong>💡 Examples:</strong></p>
            <ul>
              <li>"Father of Mohammad who attends Friday prayers"</li>
              <li>"Parent of Ali in Sunday school"</li>
              <li>"Volunteer at community events"</li>
            </ul>
            <p className="form-hint">
              ℹ️ If we recognize you from your description, you'll be approved immediately!
              Otherwise, we may give you a quick call to verify for community safety.
            </p>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="modal-footer">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setStep('email');
            setPassword('');
            setConfirmPassword('');
            setError('');
          }}
        >
          Back
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </div>

      <div className="privacy-note">
        <p>
          🔒 By creating an account, you agree to receive service notifications via email.
          You can unsubscribe anytime.
        </p>
      </div>
    </form>
  );

  const renderProfileStep = () => (
    <form onSubmit={handleCompleteProfile}>
      <div className="profile-form-intro">
        <p><strong>🎉 Welcome to Our Community!</strong></p>
        <p>Thanks for verifying your email! Let's complete your profile so community members can recognize you.</p>
        <p className="form-hint">This is a one-time setup - you won't need to do this again.</p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>First Name *</label>
          <input
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="form-input"
            autoFocus
          />
        </div>
        <div className="form-group">
          <label>Last Name *</label>
          <input
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            className="form-input"
          />
        </div>
      </div>
      <p className="form-hint" style={{ marginTop: '-15px', marginBottom: '15px' }}>
        Only your last initial will be shown publicly (e.g., "John D.")
      </p>

      <div className="form-group">
        <label>Phone Number *</label>
        <input
          type="tel"
          placeholder="+1 (555) 123-4567"
          value={phoneNumber}
          onChange={(e) => {
            setPhoneNumber(e.target.value);
            if (whatsappSameAsPhone) {
              setWhatsappNumber(e.target.value);
            }
          }}
          required
          className="form-input"
        />
        <p className="form-hint" style={{ marginTop: '5px', marginBottom: '0' }}>
          We may call you to verify your profile for community safety
        </p>
      </div>

      {/* WhatsApp Number Field */}
      <div className="form-group">
        <label>WhatsApp Number (Optional)</label>
        <input
          type="tel"
          placeholder="+1 (555) 987-6543"
          value={whatsappNumber}
          onChange={(e) => setWhatsappNumber(e.target.value)}
          disabled={whatsappSameAsPhone}
          className="form-input"
          style={{
            background: whatsappSameAsPhone ? '#f5f5f5' : 'white',
            cursor: whatsappSameAsPhone ? 'not-allowed' : 'text'
          }}
        />

        {/* Toggle: Same as Phone Number */}
        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="checkbox"
            id="whatsapp-same"
            checked={whatsappSameAsPhone}
            onChange={(e) => handleWhatsappCheckboxChange(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <label
            htmlFor="whatsapp-same"
            style={{ cursor: 'pointer', fontSize: '0.95rem', margin: 0 }}
          >
            Same as phone number
          </label>
        </div>

        <p className="form-hint" style={{ marginTop: '5px' }}>
          Community members will contact you via WhatsApp when responding to your services
        </p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            placeholder="San Francisco"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>State</label>
          <input
            type="text"
            placeholder="CA"
            value={state}
            onChange={(e) => setState(e.target.value.slice(0, 2).toUpperCase())}
            className="form-input"
            maxLength="2"
            style={{ textTransform: 'uppercase' }}
          />
        </div>
      </div>

      <div className="form-group">
        <label>How would you describe yourself?</label>
        <textarea
          placeholder="Describe yourself in 1-2 lines so community members recognize you..."
          value={recognitionDescription}
          onChange={(e) => setRecognitionDescription(e.target.value)}
          className="form-input form-textarea"
          rows="3"
          maxLength="200"
        />
        <div className="recognition-examples">
          <p><strong>Examples:</strong></p>
          <ul>
            <li>"Parent of Ali who attends Sunday school"</li>
            <li>"Usually volunteer at community events"</li>
          </ul>
          <p className="form-hint">
            If we recognize you, we'll approve immediately. If not, we may give you a quick call to verify.
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="modal-footer">
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Complete Profile'}
        </button>
      </div>
    </form>
  );

  // OTP verification success handler
  const handleOTPSuccess = async (session) => {
    console.log('[OTP] Verification successful, user signed in:', session.user.email);

    // Clear temporary password
    setTempPassword('');

    // User is now signed in with full session
    if (session?.user) {
      // Update verification status in database
      const { data: verifyResult, error: verifyError } = await supabase.rpc('complete_otp_verification', {
        p_user_id: session.user.id
      });

      if (verifyError) {
        console.error('[OTP] Failed to update verification status:', verifyError);
      } else {
        console.log('[OTP] Verification status updated:', verifyResult);
      }

      // Trigger authenticated user handler - MUST AWAIT to ensure completion
      await handleAuthenticatedUser(session.user);
      console.log('[handleOTPSuccess] ✅ handleAuthenticatedUser completed successfully');
      console.log('[handleOTPSuccess] Checking member info in localStorage:',
        servicesService.getMemberInfo());
      console.log('[handleOTPSuccess] Member data fetched and onSuccess called, now closing modals');

      // Get member info
      const memberData = servicesService.getMemberInfo();
      const displayData = {
        name: session.user.user_metadata?.first_name || memberData?.name || 'User',
        email: session.user.email,
        verified: true,
        message: `Welcome! Your email is verified and you're signed in.`
      };

      // Close OTP modal
      setShowOTPModal(false);

      // Note: onSuccess() is already called by handleAuthenticatedUser() with correct member data
      // Don't call onSuccess() again here to avoid overwriting with wrong data structure

      // CRITICAL: Close the entire auth modal so parent UI updates
      // This now happens AFTER handleAuthenticatedUser completes
      onClose();

      // Note: Welcome message removed to avoid confusion
      // User is now signed in and can see their profile
    }
  };

  // Show OTP verification modal
  if (showOTPModal) {
    return (
      <>
        <OTPVerificationModal
          show={showOTPModal}
          onHide={() => {
            // Don't allow closing - verification is required
            // User can only close by completing verification or resending code
          }}
          email={otpEmail}
          centerName={config?.name || 'Community Services'}
          onSuccess={handleOTPSuccess}
        />
      </>
    );
  }

  // Show email confirmation modal if registration requires email verification
  if (showEmailConfirmation) {
    return (
      <div className="modal-overlay" onClick={() => setShowEmailConfirmation(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', textAlign: 'center' }}>
          <div className="modal-header">
            <h2>Check Your Email</h2>
            <button className="modal-close" onClick={() => setShowEmailConfirmation(false)}>×</button>
          </div>

          <div className="modal-body" style={{ padding: '32px 24px' }}>
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '48px',
              color: 'white'
            }}>
              ✓
            </div>

            {/* Message */}
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '12px' }}>
              Thank You for Registering!
            </h3>

            <p style={{ fontSize: '16px', color: '#6B7280', marginBottom: '24px', lineHeight: '1.5' }}>
              We sent a confirmation link to:
            </p>

            <div style={{
              backgroundColor: '#F3F4F6',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '16px',
              fontWeight: '500',
              color: '#1F2937',
              wordBreak: 'break-word'
            }}>
              {registeredEmail}
            </div>

            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px', lineHeight: '1.5' }}>
              Please check your email and click the confirmation link to activate your account.
              You can close this window and return after confirming.
            </p>

            {/* Close Button */}
            <button
              className="btn-primary"
              onClick={() => {
                setShowEmailConfirmation(false);
                onClose();
              }}
              style={{ width: '100%', padding: '12px', fontSize: '16px', fontWeight: '600' }}
            >
              Got It
            </button>

            <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '16px' }}>
              Didn't receive the email? Check your spam folder or contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show account created success modal
  if (showAccountCreated) {
    return (
      <div className="modal-overlay" onClick={() => setShowAccountCreated(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', textAlign: 'center' }}>
          <div className="modal-header">
            <h2>Welcome to {config?.name || 'Community Services'}!</h2>
            <button className="modal-close" onClick={() => setShowAccountCreated(false)}>×</button>
          </div>

          <div className="modal-body" style={{ padding: '32px 24px' }}>
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '48px',
              color: 'white'
            }}>
              ✓
            </div>

            {/* Success Message */}
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '12px' }}>
              Your Profile Has Been Created!
            </h3>

            <div style={{
              backgroundColor: '#D1FAE5',
              border: '1px solid #6EE7B7',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#064E3B', marginBottom: '8px' }}>
                ✅ You can start posting services immediately!
              </p>
              <p style={{ fontSize: '14px', color: '#047857', lineHeight: '1.5' }}>
                Your account is active and ready to use. Browse and post community services right away.
              </p>
            </div>

            {/* Email Verification Notice */}
            <div style={{
              backgroundColor: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#78350F', marginBottom: '8px' }}>
                📧 Email Verification Required
              </p>
              <p style={{ fontSize: '14px', color: '#92400E', lineHeight: '1.5' }}>
                Please verify your email within 7 days to maintain access.
                We've sent a verification link to:
              </p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#78350F', marginTop: '8px', wordBreak: 'break-word' }}>
                {registeredEmail}
              </p>
            </div>

            {/* Continue Button */}
            <button
              className="btn-primary"
              onClick={() => {
                setShowAccountCreated(false);
                if (createdMemberData) {
                  onSuccess(createdMemberData);
                }
                onClose();
              }}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                fontWeight: '600',
                backgroundColor: 'var(--center-primary-color, #1e4273)'
              }}
            >
              Start Using Services
            </button>

            <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '16px' }}>
              Check your email later to complete verification. You can use the platform immediately!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {step === 'signup' ? 'Join Our Community' : step === 'profile' ? 'Complete Your Profile' : 'Sign In to Community Services'}
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {step === 'email' && renderEmailStep()}
          {step === 'signup' && renderCombinedSignUpForm()}
          {step === 'profile' && renderProfileStep()}
        </div>
      </div>
    </div>
  );
}

export default ServiceAuthModal;
